import { APIService } from "@/services/api";
import type { ChatSession } from "@/models/chat-session";
import type { ChatMessage, LLMLevel } from "@/models/chat-message";
import type { 
  SendMessageRequest,
  ListChatSessionsResponse
} from "@/services/api.models";
import type {CursorToken} from "@/models";

// Video Chat specific response interfaces
export interface VideoChatSendMessageResponse {
  userMessage: ChatMessage;
  chatSession: ChatSession;
}

export interface VideoChatListMessagesResponse {
  messages: ChatMessage[];
  chatSession?: ChatSession; // Optional since backend might not always include it
  next: CursorToken;
  total?: number;
}

/**
 * Video Chat API Service
 * Handles video-specific chat functionality using the unified chat system
 * Video chats are regular ChatSessions with type='VIDEO_SESSION' and videoKnowledgeID set
 * Uses the unified /users/self/sessions API instead of separate video-chat endpoints
 */
export class VideoChatAPI {
  
  /**
   * Get existing video chat session for a specific video or create new one
   * Backend automatically creates session on first access
   */
  static async getOrCreateSession(videoId: string, forceNew = false): Promise<ChatSession> {
    return APIService.inst.apiFetch<void, ChatSession>(
      'GET', 
      '/videos/:vid/chat', 
      {
        urlParams: { vid: videoId },
        queryParams: forceNew ? { forceNew: 'true' } : undefined,
        auth: true
      }
    );
  }


  /**
   * Send a message in a video chat session
   * Now uses the unified chat API instead of separate video-chat endpoints
   */
  static async sendMessage(
    sessionId: string,
    content: string,
    images?: string[],
    llmLevel?: LLMLevel,
    allowTools?: boolean
  ): Promise<VideoChatSendMessageResponse> {
    return APIService.inst.apiFetch<SendMessageRequest, VideoChatSendMessageResponse>(
      'POST',
      '/users/self/sessions/:sid/messages',
      {
        urlParams: { sid: sessionId },
        body: { content, images, llmLevel, allowTools },
        auth: true
      }
    );
  }

  /**
   * List messages in a video chat session
   * Now uses the unified chat API instead of separate video-chat endpoints
   */
  static async listMessages(
    sessionId: string, 
    _after?: string,
    _all?: boolean,
    debugMode?: boolean
  ): Promise<VideoChatListMessagesResponse> {
    return APIService.inst.apiFetch<void, VideoChatListMessagesResponse>(
      'GET', 
      '/users/self/sessions/:sid/messages', 
      {
        urlParams: { sid: sessionId },
        queryParams: {
          all: debugMode // Use 'all' parameter like normal chat API
        },
        auth: true
      }
    );
  }

  /**
   * Get a specific video chat session
   * Now uses the unified chat API instead of separate video-chat endpoints
   */
  static async getSession(sessionId: string): Promise<ChatSession> {
    return APIService.inst.apiFetch<void, ChatSession>(
      'GET', 
      '/users/self/sessions/:sid', 
      {
        urlParams: { sid: sessionId },
        auth: true
      }
    );
  }

  /**
   * List all video chat sessions for the current user
   * Now uses the unified chat API and filters by VIDEO_SESSION type
   */
  static async listSessions(after?: string): Promise<ListChatSessionsResponse> {
    return APIService.inst.apiFetch<void, ListChatSessionsResponse>(
      'GET', 
      '/users/self/sessions', 
      {
        queryParams: { 
          after,
          type: 'VIDEO_SESSION' // Filter for video chat sessions only
        },
        auth: true
      }
    );
  }

  /**
   * Close a video chat session
   * Now uses the unified chat API close endpoint
   */
  static async closeSession(sessionId: string): Promise<void> {
    return APIService.inst.apiFetch<void, void>(
      'POST', 
      '/users/self/sessions/:sid/close', 
      {
        urlParams: { sid: sessionId },
        auth: true
      }
    );
  }

  /**
   * Delete a video chat session (kept for compatibility)
   * Note: Close is preferred over delete in the unified system
   */
  static async deleteSession(sessionId: string): Promise<void> {
    return this.closeSession(sessionId);
  }
}